/**
 * Weekly capacity — the denominator every "of Xh" on the timesheet is read
 * against.
 *
 * This app bills on these hours, so the target in the largest type on the page
 * has to be the person's own contracted week, not a hardcoded 40. A 16h
 * contractor who has logged 3.25h is 20% through their week, not 8%.
 *
 * `Person.weeklyCapacityHours` is null/0 for on-demand people with no committed
 * capacity (the same rule the team module's utilization uses). That is NOT
 * "zero hours expected" — it means there is no target at all, and the UI must
 * say so instead of inventing one.
 */

import { formatHours } from '../../lib/format';

export interface WeekCapacity {
  /** Contracted hours per week, or null when nothing is committed. */
  weekly: number | null;
  /**
   * Contracted hours per working day = weekly ÷ 5, matching
   * `capacityHoursFor()` in the team service (business days, never calendar).
   */
  daily: number | null;
  /** "16 h" / "40 h" — never rendered when `weekly` is null. */
  weeklyLabel: string;
  /** "3.2 h" / "8 h" — never rendered when `daily` is null. */
  dailyLabel: string;
}

export function weekCapacity(person: { weeklyCapacityHours?: number | null } | null | undefined): WeekCapacity {
  const weekly =
    person?.weeklyCapacityHours != null && person.weeklyCapacityHours > 0
      ? person.weeklyCapacityHours
      : null;
  const daily = weekly === null ? null : Math.round((weekly / 5) * 100) / 100;
  return {
    weekly,
    daily,
    // `formatHours(…, { unit: true })` — the app's one hours unit, so the
    // timesheet target reads the same as the same figure on /team and /support.
    weeklyLabel: weekly === null ? '' : formatHours(weekly, { unit: true }),
    dailyLabel: daily === null ? '' : formatHours(daily, { unit: true }),
  };
}
